import IconButton from '@/components/Buttons/IconButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import Heart from '@/images/Heart.svg';
import HeartFilled from '@/images/HeartFilled.svg';
import { useAuth } from '@/services/auth';
import { useUserSettings } from '@/state/user-settings/UserSettingsProvider';
import { useUpdateUserSettings } from '@/state/user-settings/useUpdateUserSettings';
import theme from '@/theme';
import { IVoice } from '@d-id/types';
import styled from '@emotion/styled';
import CircularProgress from '@mui/material/CircularProgress';
import { useRecoilState } from 'recoil';
import { ttsIsMutatingFavourites } from '../../VoiceModal';

const StyledIconButton = styled(IconButton)<{ active?: boolean }>`
    color: ${theme.colors.secondary[900]} !important;

    ${({ active }) =>
        active &&
        `
      color: ${theme.colors.primary[900]} !important;
  `}

    &.MuiButtonBase-root.Mui-disabled.MuiIconButton-root {
        opacity: 0.5;
        pointer-events: all;
    }
`;

interface Props {
    voice: IVoice;
}

export const FavouriteButton = ({ voice }: Props) => {
    const { isAnonymous } = useAuth();
    const [isMutating, setIsMutating] = useRecoilState(ttsIsMutatingFavourites);
    const { mutateAsync: updateUserSettings, isLoading: updateLoading } = useUpdateUserSettings();
    const { userSettings } = useUserSettings();

    const favouriteVoices = userSettings?.preferences?.voices || [];
    const isFavourite = favouriteVoices.includes(`${voice.locale}-${voice.id}`);

    const handleAddToFavourites = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setIsMutating(true);
        e.stopPropagation();

        const updatedVoices = [...favouriteVoices, `${voice.locale}-${voice.id}`];
        await updateUserSettings({ preferences: { voices: updatedVoices } });
        setIsMutating(false);
    };

    const handleRemoveFromFavourites = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setIsMutating(true);
        e.stopPropagation();

        const updatedVoices = favouriteVoices.filter((v: string) => v !== `${voice.locale}-${voice.id}`);
        await updateUserSettings({ preferences: { voices: updatedVoices } });
        setIsMutating(false);
    };

    if (isAnonymous) {
        return null;
    }

    return (
        <StyledIconButton
            ariaLabel="favourite"
            active={isFavourite}
            onClick={isFavourite ? handleRemoveFromFavourites : handleAddToFavourites}
            disabled={updateLoading || isMutating}>
            {updateLoading ? <CircularProgress size={24} /> : <MaskedIcon src={isFavourite ? HeartFilled : Heart} size="24px" />}
        </StyledIconButton>
    );
};
